// ───────────────────────────────────────────────
// 📦 VideoBackgroundCapturePortal.jsx
// - 동영상에서 앞뒤 프레임 추출 + FFT + 파형 캡처
// - 총 6장 이미지 생성 후 Node → Python 자동 전송
// ───────────────────────────────────────────────

import { useEffect, useRef, useState } from "react";
import { getFrameFft } from "lib/api/getFrameFft";
import { postSaveFrameFftInfo } from "lib/api/postSaveFrameFftInfo";
import FftBarGraph from "./FftBarGraph";
import Waveform from "./WaveFrom";
import html2canvas from "html2canvas";

// ───────────────────────────────────────────────
// 📦 메인 컴포넌트
// ───────────────────────────────────────────────
export default function VideoBackgroundCapturePortal({
  file,
  setCapturePotalStatus,
  fetchGetFile,
  locale,
}) {
  const containerRef = useRef(null);
  const graph_ref_1 = useRef();
  const graph_ref_2 = useRef();
  const waveform_ref_1 = useRef();
  const waveform_ref_2 = useRef();
  const [frames, setFrames] = useState([]);
  const [frameImgs, setFrameImgs] = useState([]);

  // ───────────────────────────────────────────────
  // STEP 1️⃣ : 비디오 로드 및 프레임 추출
  // ───────────────────────────────────────────────
  useEffect(() => {
    if (!file?.video_mp4 || !file?.video_decoded_wav_f32) return;

    console.log(
      "🎞️ [STEP 1] VideoBackgroundCapturePortal 진입:",
      file.video_mp4,
    );

    const video = document.createElement("video");
    video.src = file.video_mp4;
    video.crossOrigin = "use-credentials";
    video.muted = true;
    video.preload = "auto";
    video.style.display = "none";
    containerRef.current?.appendChild(video);

    const captureFrameAt = (time) =>
      new Promise((resolve) => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const onSeeked = () => {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
          resolve(dataUrl);
        };

        video.addEventListener("seeked", onSeeked, { once: true });
        video.currentTime = time;
      });

    const startProcess = async () => {
      await new Promise((resolve) =>
        video.addEventListener("loadedmetadata", resolve, { once: true }),
      );

      const duration = video.duration;
      if (!duration || duration <= 1) return;

      const times = [0.1, duration - 0.1];
      const formattedTimes = times.map((t) => formatTime(t));
      console.log("🎯 [STEP 1-2] 캡처 시점:", formattedTimes);

      const temp = [];

      // ───────────────────────────────────────────────
      // STEP 1-A : 첫 번째 프레임 (앞쪽)
      // ───────────────────────────────────────────────
      const time1 = times[0];
      const formatted1 = formattedTimes[0];
      console.log(`🎬 [STEP 1-3-1] 프레임 캡처 (${formatted1})`);

      const frameImg1 = await captureFrameAt(time1);
      setFrameImgs((prev) => {
        return [...prev, frameImg1];
      });

      const res1 = await getFrameFft(file?.upload_id, formatted1);
      const data1 = res1?.success ? res1.data : null;

      if (!res1?.success) {
        console.warn("⚠️ [STEP 1-3-1] 첫 번째 FFT 요청 실패 → 프로세스 중단");
        setFrames([]); // 초기화 or 그대로 두기
        return; // ❌ 두 번째 요청으로 진행하지 않음
      }

      temp.push({
        time: formatted1,
        timeToFloat: time1,
        image: frameImg1,
        decibel: data1?.detected_decibel ?? null,
        distance: data1?.distance_value ?? null,
        fft_array: data1?.fft_array ?? [],
      });

      // ───────────────────────────────────────────────
      // STEP 1-B : 두 번째 프레임 (뒤쪽) — 첫 번째 성공 시만 실행
      // ───────────────────────────────────────────────
      const time2 = times[1];
      const formatted2 = formattedTimes[1];
      console.log(`🎬 [STEP 1-3-2] 프레임 캡처 (${formatted2})`);

      const frameImg2 = await captureFrameAt(time2);
      setFrameImgs((prev) => {
        return [...prev, frameImg2];
      });

      const res2 = await getFrameFft(file?.upload_id, formatted2);
      const data2 = res2?.success ? res2.data : null;

      if (!res2?.success) {
        console.warn("⚠️ [STEP 1-3-2] 두 번째 FFT 요청 실패");
      }

      temp.push({
        time: formatted2,
        timeToFloat: time2,
        image: frameImg2,
        decibel: data2?.detected_decibel ?? null,
        distance: data2?.distance_value ?? null,
        fft_array: data2?.fft_array ?? [],
      });

      // ───────────────────────────────────────────────
      // STEP 1-C : 상태 저장
      // ───────────────────────────────────────────────
      console.log("✅ [STEP 1-4] 프레임/FFT 데이터 수집 완료:", temp);
      setFrames(temp);
    };
    startProcess();

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
    };
  }, [file?.video_mp4, file?.upload_id]);

  // ───────────────────────────────────────────────
  // STEP 2️⃣ : FFT 그래프 + 파형 렌더링 후 캡처
  // ───────────────────────────────────────────────
  useEffect(() => {
    if (frames.length !== 2) return;

    (async () => {
      console.log("🧩 [STEP 2] 그래프 및 파형 캡처 시작");

      const captureRefImage = async (ref) => {
        if (!ref?.current) return null;

        // ✅ 렌더링 안정화 딜레이 증가
        await new Promise((r) => setTimeout(r, 350));

        const canvas = await html2canvas(ref.current, {
          scale: 1,
          useCORS: true,
          allowTaint: true,
          backgroundColor: null,
          logging: false,
        });

        return canvas.toDataURL("image/png");
      };

      const [fftGraphFront, fftGraphBack, waveformFront, waveformBack] =
        await Promise.all([
          captureRefImage(graph_ref_1),
          captureRefImage(graph_ref_2),
          captureRefImage(waveform_ref_1),
          captureRefImage(waveform_ref_2),
        ]);

      const merged = [
        { ...frames[0], graph: fftGraphFront, waveform: waveformFront },
        { ...frames[1], graph: fftGraphBack, waveform: waveformBack },
      ];

      const times = [frames[0].time, frames[1].time];

      console.log("✅ [STEP 2] 총 6장 캡처 완료 (앞3 + 뒤3)");

      console.log("🚀 [STEP 3] Node 서버로 전송 시작...");

      console.log(`*********times*********** ${times}`);
      console.log(`*********merged*********** ${merged}`);
      console.log(`*********frames*********** ${frames}`);
      const { success, message } = await postSaveFrameFftInfo({
        upload_id: file?.upload_id,
        clean_file_name: file?.clean_file_name,
        times,
        frames: merged,
        volume: file?.volume,
        locale,
      });

      if (success) console.log("✅ Python 서   전송 성공:", message);
      else console.error("❌ Python 서버 전송 실패:", message);
      fetchGetFile();
      setCapturePotalStatus(false);
    })();
  }, [frames]);

  // ───────────────────────────────────────────────
  // STEP 4️⃣ : 비가시 영역 렌더링 (html2canvas 대상)
  // ───────────────────────────────────────────────
  return (
    <div
      ref={containerRef}
      style={{
        position: "absolute",
        top: "-9999px",
        left: "-9999px",
        visibility: "visible",
      }}
    >
      <div>
        <img src={frameImgs[0]} alt="" />
        <FftBarGraph
          ref={graph_ref_1}
          data={frames[0]?.fft_array}
          isCapture
        />
        <Waveform
          pcmDataUrl={file?.video_decoded_wav_f32}
          time={frames[0]?.timeToFloat}
          captureRef={waveform_ref_1}
        />
      </div>
      <div>
        <img src={frameImgs[1]} alt="" />
        <FftBarGraph
          ref={graph_ref_2}
          data={frames[1]?.fft_array}
          isCapture
        />
        <Waveform
          pcmDataUrl={file?.video_decoded_wav_f32}
          time={frames[1]?.timeToFloat}
          captureRef={waveform_ref_2}
        />
      </div>
    </div>
  );
}

export function formatTime(sec) {
  if (typeof sec === "string" && /^\d{2}:\d{2}\.\d{2}$/.test(sec)) {
    return sec;
  }

  const value = Number(sec);
  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60);
  const fraction = Math.round((value % 1) * 100);

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
    2,
    "0",
  )}.${String(fraction).padStart(2, "0")}`;
}
